import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ROTAS } from '@remoto/shared';
import { chamar } from '../api';
import { socketDoPainel } from '../socket/painel';

/**
 * "Estar com a conversa aberta e a aba à frente já é ter visto."
 *
 * A regra é da P2 e não mudou. O que mudou é DE ONDE vem o fim de turno que
 * ela observa.
 *
 * A primeira versão só enxergava `ultimoTurnoFimEm` pelo giro de 15 s da
 * consulta `['sessao', id]`, enquanto a lista descobria a novidade no giro de
 * 10 s da dela. Nessa janela — a lista já sabe, a conversa ainda não — quem
 * lia e saía deixava a bolinha acesa sobre o que acabara de ler; e ela nunca
 * mais se apagava sozinha, porque o painel da conversa era desmontado antes do
 * giro que a apagaria. Era esse o acúmulo que o dono relatou.
 *
 * Só que o servidor já conta o fato na hora: ao fechar um turno ele emite
 * `sessao.estado` com o carimbo novo para TODO painel conectado
 * (`PainelGateway.publicarSessao`). Aqui a gente escuta esse evento em vez de
 * esperar a sondagem.
 *
 * Duas guardas continuam valendo:
 *
 * - **Só com a aba à frente.** Uma janela esquecida no segundo monitor apagaria
 *   a bolinha de algo que ninguém leu. A diferença é que agora, quando a aba
 *   volta, a marca é reposta: antes o efeito desistia calado e não tentava mais,
 *   porque as dependências não mudavam depois disso — a mesma armadilha que o
 *   Wake Lock pagou na P3.
 * - **A marca é do servidor**, para o aviso sumir também nos outros aparelhos
 *   da mesma pessoa. Quem decide o valor gravado é `marcarVista`, que relê o
 *   `ultimoTurnoFimEm` da linha: um turno que termine entre a leitura e a
 *   gravação continua não-visto, como manda o ADR-0011.
 */
export function usarMarcarVista(
  sessaoId: string,
  ultimoTurnoFimEm: string | null | undefined,
): void {
  const cliente = useQueryClient();

  /*
    O fim de turno vivo.

    `sessao.estado` também carrega renomear e arquivar, que não trazem carimbo
    nenhum — daí a exigência de `ultimoTurnoFimEm` no quadro antes de escrever.
  */
  useEffect(() => {
    if (!sessaoId) return;
    const socket = socketDoPainel();
    const aoMudar = (dados: { id?: string; ultimoTurnoFimEm?: string | null }): void => {
      if (dados.id !== sessaoId || !dados.ultimoTurnoFimEm) return;
      cliente.setQueryData<{ ultimoTurnoFimEm?: string | null }>(['sessao', sessaoId], (atual) =>
        atual ? { ...atual, ultimoTurnoFimEm: dados.ultimoTurnoFimEm } : atual,
      );
    };
    socket.on('sessao.estado', aoMudar);
    return () => {
      socket.off('sessao.estado', aoMudar);
    };
  }, [sessaoId, cliente]);

  /*
    A chave carrega o id junto com o carimbo.

    Só o carimbo bastaria enquanto o componente fosse remontado a cada troca de
    conversa — e ele não é: no painel de três colunas o `PainelDaSessao` é
    reaproveitado, e uma referência que sobrevive à troca é uma referência que
    fala de outra sessão.
  */
  const marcado = useRef('');
  useEffect(() => {
    const fim = ultimoTurnoFimEm ?? null;
    if (!sessaoId || !fim) return;
    const chave = sessaoId + '|' + fim;

    const marcar = (): void => {
      if (marcado.current === chave) return;
      if (document.visibilityState !== 'visible') return;
      marcado.current = chave;
      void chamar(`${ROTAS.sessoes}/${sessaoId}/vista`, { method: 'POST' }).catch(() => {
        // Marcar como vista é conveniência: falhar aqui não pode atrapalhar a
        // conversa. Solta a chave para a próxima oportunidade tentar de novo.
        if (marcado.current === chave) marcado.current = '';
      });
    };

    marcar();
    document.addEventListener('visibilitychange', marcar);
    return () => {
      document.removeEventListener('visibilitychange', marcar);
    };
  }, [sessaoId, ultimoTurnoFimEm]);
}
